import { useState, useCallback, useEffect, useMemo } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import type {
  CreateWebsiteBannerDto,
  WebsiteBannerDto,
} from '@/generated/korza-api-client/types.gen';
import {
  DEFAULT_BANNER,
  bannerDtoToForm,
} from '@/lib/website-banner-constants';

export type SetPreviewFormSnapshotRef = React.MutableRefObject<
  ((value: CreateWebsiteBannerDto) => void) | null
>;

export interface UseWebsiteBannerFormParams {
  bannerData: WebsiteBannerDto | null | undefined;
  bannerId: string | null;
  saveMutation: UseMutationResult<
    WebsiteBannerDto,
    Error,
    CreateWebsiteBannerDto
  >;
  setPreviewFormSnapshotRef: SetPreviewFormSnapshotRef;
  LL: {
    websiteBannerPage: {
      saveConfirm: {
        publishTitle: () => string;
        publishDescription: () => string;
        updateTitle: () => string;
        updateDescription: () => string;
        disableTitle: () => string;
        disableDescription: () => string;
      };
      footer: {
        saveAndDisable: () => string;
        publish: () => string;
        save: () => string;
      };
    };
  };
}

export function useWebsiteBannerForm({
  bannerData,
  bannerId,
  saveMutation,
  setPreviewFormSnapshotRef,
  LL,
}: UseWebsiteBannerFormParams) {
  const [form, setForm] = useState<CreateWebsiteBannerDto>(DEFAULT_BANNER);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const baselineForm = useMemo(() => {
    if (bannerData) return bannerDtoToForm(bannerData);
    return DEFAULT_BANNER;
  }, [bannerData]);

  useEffect(() => {
    if (bannerData) {
      const next = bannerDtoToForm(bannerData);
      setForm(next);
      setPreviewFormSnapshotRef.current?.(next);
    } else if (!bannerId) {
      setForm(DEFAULT_BANNER);
    }
  }, [bannerId, bannerData, setPreviewFormSnapshotRef]);

  const updateContent = useCallback(
    (patch: Partial<CreateWebsiteBannerDto['content']>) => {
      setForm((prev) => ({
        ...prev,
        content: { ...prev.content, ...patch },
      }));
    },
    []
  );

  const updateTheme = useCallback(
    (patch: Partial<CreateWebsiteBannerDto['theme']>) => {
      setForm((prev) => ({
        ...prev,
        theme: { ...prev.theme, ...patch },
      }));
    },
    []
  );

  const updateCustomStyles = useCallback(
    (patch: Partial<CreateWebsiteBannerDto['customStyles']>) => {
      setForm((prev) => ({
        ...prev,
        customStyles: { ...prev.customStyles, ...patch },
      }));
    },
    []
  );

  const updatePosition = useCallback(
    (patch: Partial<NonNullable<CreateWebsiteBannerDto['position']>>) => {
      setForm((prev) => ({
        ...prev,
        position: {
          ...(prev.position ?? {
            placeInside: null,
            placeAfter: null,
            placeBefore: null,
          }),
          ...patch,
        },
      }));
    },
    []
  );

  const handleDiscard = useCallback(() => {
    setForm(baselineForm);
  }, [baselineForm]);

  const baselineEnabled = baselineForm.enabled ?? false;
  const formEnabled = form.enabled ?? false;
  const needsConfirmation = baselineEnabled || formEnabled;

  const saveConfirmContent = useMemo(() => {
    if (!baselineEnabled && formEnabled) {
      return {
        title: LL.websiteBannerPage.saveConfirm.publishTitle(),
        description: LL.websiteBannerPage.saveConfirm.publishDescription(),
      };
    }
    if (baselineEnabled && formEnabled) {
      return {
        title: LL.websiteBannerPage.saveConfirm.updateTitle(),
        description: LL.websiteBannerPage.saveConfirm.updateDescription(),
      };
    }
    return {
      title: LL.websiteBannerPage.saveConfirm.disableTitle(),
      description: LL.websiteBannerPage.saveConfirm.disableDescription(),
    };
  }, [baselineEnabled, formEnabled, LL]);

  const handleSave = useCallback(() => {
    if (needsConfirmation) {
      setShowSaveConfirm(true);
    } else {
      saveMutation.mutate(form);
    }
  }, [needsConfirmation, saveMutation, form]);

  const handleConfirmSave = useCallback(() => {
    setShowSaveConfirm(false);
    saveMutation.mutate(form);
  }, [saveMutation, form]);

  const primaryButtonLabel = useMemo(() => {
    if (baselineEnabled && !formEnabled) {
      return LL.websiteBannerPage.footer.saveAndDisable();
    }
    if (formEnabled) {
      return LL.websiteBannerPage.footer.publish();
    }
    return LL.websiteBannerPage.footer.save();
  }, [baselineEnabled, formEnabled, LL]);

  const isDirty = JSON.stringify(form) !== JSON.stringify(baselineForm);

  return {
    form,
    setForm,
    baselineForm,
    updateContent,
    updateTheme,
    updateCustomStyles,
    updatePosition,
    isDirty,
    handleDiscard,
    showSaveConfirm,
    setShowSaveConfirm,
    needsConfirmation,
    saveConfirmContent,
    primaryButtonLabel,
    handleSave,
    handleConfirmSave,
    formEnabled,
    baselineEnabled,
  };
}
